import { useCallback, useMemo, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useLocation } from "react-router";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { ControlePatrimonioDetalhes } from "../../domain/models/controle_patrimonio_detalhes";
import { useMaquinasEquipCadastroPrincipalPagination } from "../hooks/Maquinasequip_cadastroprincipal/pagination";
import { ControlePatrimonioArquivoLista } from "../../domain/models/controle_patrimonio_arquivo_lista";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { initialMaquinasEquipForm, MaquinasequipForm, MaquinasequipSchema } from "../schemas/Maquinasequip_cadastro/Maquinasequip_cadastro";
import { FornecedorDropDown } from "../../../../common/domain/models/fornecedor_dropdown";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { MaquinaEquipArquivoForm } from "../schemas/ControleDePatrimonioArquivo/ControleDePatrimonioArquivo";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";


type LoadControlePatrimonioArquivosState = {
  idMaquinaEquip?: number;
  Page?: number | null;
  RowsPerPage?: number | null;
};


export const useMaquinasEquipCadastroPrincipalViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoByParentUseCase,
      getAllLogAlteracaoUseCase,
    },
    controlePatrimonio: {
      getControledePatrimonioByIdUseCase,
      getAllControlePatrimonioArquivoUseCase,
      createControlePatrimonioUseCase,
      updateControlePatrimonioUseCase,
      downloadControlePatrimonioArquivoUseCase,
      createMaquinaEquipArquivoUseCase,
      updateMaquinaEquipArquivoUseCase,
      deleteMaquinaEquipArquivoUseCase,
    },
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
    loadFornecedoresDropDown,
    loadFuncionariosDropDown,
  } = useDropDownLoader();

  const { pushErrorNotification, pushInfoNotification, setPageSaving } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idControlePatrimonio, setIdControlePatrimonio] = useState<number>();
  const [controlePatrimonio, setControlePatrimonio] = useState<ControlePatrimonioDetalhes>();
  const [controlePatrimonioArquivos, setControlePatrimonioArquivos] = useState<ControlePatrimonioArquivoLista[]>();

  const [selectedControlePatrimonioArquivo, setSelectedControlePatrimonioArquivo] = useState<ControlePatrimonioArquivoLista>();

  const [controlePatrimonioArquivoModal, setControlePatrimonioArquivoModal] = useState(false);
  const [controlePatrimonioArquivoDeleteModal, setControlePatrimonioArquivoDeleteModal] = useState(false);

  const [centroCustoDropdown, setCentroCustoDropdown] = useState<CentroCustoDropDown[]>();
  const [fornecedorDropdown, setFornecedorDropdown] = useState<FornecedorDropDown[]>();
  const [funcionarioDropdown, setFuncionarioDropdown] = useState<FuncionarioDropDown[]>();

  const pagination = useMaquinasEquipCadastroPrincipalPagination();

  const hookForm = useForm<MaquinasequipForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: { ...initialMaquinasEquipForm },
    resolver: zodResolver(MaquinasequipSchema),
  });

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  const funcionarioMonitDropdown = useMemo(() => {
    if (funcionarioDropdown === undefined) return null;
    return funcionarioDropdown.filter(x => {
      if (controlePatrimonio) {
        if (x.idFuncionario === controlePatrimonio.idFuncionarioMonit) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [
    controlePatrimonio,
    funcionarioDropdown,
  ]);

  const funcionarioRespDropdown = useMemo(() => {
    if (funcionarioDropdown === undefined) return null;
    return funcionarioDropdown.filter(x => {
      if (controlePatrimonio) {
        if (x.idFuncionario === controlePatrimonio.idFuncionarioResp) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [
    controlePatrimonio,
    funcionarioDropdown,
  ]);

  /**
   * Funcoes de carregamento
   */

  const loadControlePatrimonioById = useCallback(async (id?: number) => {
    try {
      const filteredIdMaquinaEquip = id ?? idControlePatrimonio;

      const response = await getControledePatrimonioByIdUseCase.getControlePatrimonioById(filteredIdMaquinaEquip);

      if (response) {
        const tipo = response.tipoMonitoramento?.toUpperCase();

        hookForm.setValue('idMaquinasequip', response.idMaquinaEquip);
        hookForm.setValue('ativo', response.ativo ? 1 : 0);
        hookForm.setValue('dataCadastro', response.dataCadastro);
        hookForm.setValue('codigo', response.codigo);
        hookForm.setValue('idCentroCusto', response.idCentroCusto);
        hookForm.setValue('descricao', response.descricao);
        hookForm.setValue('criterioAceitacao', response.criterioAceitacao);
        hookForm.setValue('referenciaAceitacao', response.referenciaAceitacao);
        hookForm.setValue('quantidade', response.quantidade);
        hookForm.setValue('dataAquisicao', response.dataAquisicao);
        hookForm.setValue('dataGarantia', response.dataGarantia);
        hookForm.setValue('descResponsavelMonit', response.descResponsavelMonit);
        hookForm.setValue('idFuncionarioMonit', response.idFuncionarioMonit);
        hookForm.setValue('idFornecedorMonit', response.idFornecedorMonit);
        hookForm.setValue('descResponsavelResp', response.descResponsavelResp);
        hookForm.setValue('idFuncionarioResp', response.idFuncionarioResp);
        hookForm.setValue('tipoMonitoramento', tipo === 'C' ? 1 : tipo === 'V' ? 0 : 2);
        hookForm.setValue('frequenciaVerificacao', response.frequenciaVerificacao);
        hookForm.setValue('dataProxVerificacao', response.dataProxVerificacao);
        hookForm.setValue('dataVerificacao', response.dataVerificacao);
        hookForm.setValue('marca', response.marca);
        hookForm.setValue('modelo', response.modelo);
        hookForm.setValue('valor', response.valor);
        hookForm.setValue('tipo', response.tipo);
        hookForm.setValue('idFornecedor', response.idFornecedor);
        hookForm.setValue('status', response.status);
        hookForm.setValue('fatorGerador', response.fatorGerador);
        hookForm.setValue('faixaDeUso', response.faixaDeUso);
        hookForm.setValue('localizacao', response.localizacao);
        hookForm.setValue('observacao', response.observacao);
        hookForm.setValue('tipoCalibradoMonit', response.tipoCalibradoMonit ?? 2);
        hookForm.setValue('preencherRespCal', response.descResponsavelMonit ? true : false);
        hookForm.setValue('preencherRespResp', response.descResponsavelResp ? true : false);
      }
      setControlePatrimonio(response);
    } catch (e: unknown) {
      setControlePatrimonio(undefined);
      throw new Error("Falha ao carregar informações de controle de patrimônio!");
    }
  }, [
    idControlePatrimonio,
    getControledePatrimonioByIdUseCase,
    hookForm,
  ]);

  const loadControlePatrimonioArquivos = useCallback(async (fs?: LoadControlePatrimonioArquivosState) => {
    try {
      const filterId = fs?.idMaquinaEquip ?? idControlePatrimonio;

      const filterPage = fs?.Page ?? pagination.page;
      const filterRowsPerPage = fs?.RowsPerPage ?? pagination.rowsPerPage;

      await getAllControlePatrimonioArquivoUseCase.getAllControlePatrimonioArquivo(
        filterId,
        filterPage,
        filterRowsPerPage,
      );

      setControlePatrimonioArquivos(getAllControlePatrimonioArquivoUseCase.items);
      pagination.setTotalItems(getAllControlePatrimonioArquivoUseCase.totalItems);
    } catch (e: unknown) {
      setControlePatrimonioArquivos([]);
      throw new Error("Falha ao buscar arquivos da máquina/equipamento!");
    }
  }, [
    idControlePatrimonio,
    pagination,
    getAllControlePatrimonioArquivoUseCase,
  ]);

  /**
   * useEffects
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get("id");
      const id = locationId ?? undefined;

      await loadFornecedoresDropDown(dt => setFornecedorDropdown(dt));
      await loadFuncionariosDropDown(dt => setFuncionarioDropdown(dt), { includeCantBeSelected: true });
      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      if (!id || id.length === 0) {
        hookForm.reset({ ...initialMaquinasEquipForm });
        return;
      }
      setIdControlePatrimonio(Number(id));

      await loadControlePatrimonioById(Number(id));
      await loadControlePatrimonioArquivos({ idMaquinaEquip: Number(id) });
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao carregar dados da máquina/equipamento!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));
      await loadFornecedoresDropDown(dt => setFornecedorDropdown(dt));
      await loadFuncionariosDropDown(dt => setFuncionarioDropdown(dt), { includeCantBeSelected: true });
      await loadControlePatrimonioById();
      await loadControlePatrimonioArquivos();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao recarregar página!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCentroCustoDropDown,
    loadFornecedoresDropDown,
    loadFuncionariosDropDown,
    loadControlePatrimonioById,
    loadControlePatrimonioArquivos,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/MaquinasEquip_lista' });
  }, [
    backOrNavigate,
  ]);

  const handleControlePatrimonioSubmit = useCallback(async (form: MaquinasequipForm) => {
    try {
      setSaving(true);
      setPageSaving(true);
      if (idControlePatrimonio) {
        await updateControlePatrimonioUseCase.updateControlePatrimonio(
          idControlePatrimonio,
          form.ativo,
          form.dataCadastro,
          form.codigo,
          form.idCentroCusto,
          form.descricao,
          form.criterioAceitacao,
          form.referenciaAceitacao,
          form.quantidade,
          form.dataAquisicao,
          form.dataGarantia,
          form.descResponsavelMonit,
          form.idFuncionarioMonit,
          form.idFornecedorMonit,
          form.descResponsavelResp,
          form.idFuncionarioResp,
          form.tipoMonitoramento,
          form.frequenciaVerificacao,
          form.dataProxVerificacao,
          form.dataVerificacao,
          form.marca,
          form.modelo,
          form.valor,
          form.tipo,
          form.idFornecedor,
          form.status,
          form.fatorGerador,
          form.faixaDeUso,
          form.localizacao,
          form.observacao,
          form.tipoCalibradoMonit,
        );

        await loadControlePatrimonioById();
      } else {
        const idMaquinaEquip = await createControlePatrimonioUseCase.createControlePatrimonio(
          form.ativo,
          form.dataCadastro,
          form.codigo,
          form.idCentroCusto,
          form.descricao,
          form.criterioAceitacao,
          form.referenciaAceitacao,
          form.quantidade,
          form.dataAquisicao,
          form.dataGarantia,
          form.descResponsavelMonit,
          form.idFuncionarioMonit,
          form.idFornecedorMonit,
          form.descResponsavelResp,
          form.idFuncionarioResp,
          form.tipoMonitoramento,
          form.frequenciaVerificacao,
          form.dataProxVerificacao,
          form.dataVerificacao,
          form.marca,
          form.modelo,
          form.valor,
          form.tipo,
          form.idFornecedor,
          form.status,
          form.fatorGerador,
          form.faixaDeUso,
          form.localizacao,
          form.observacao,
          form.tipoCalibradoMonit,
        );

        const params = new URLSearchParams(window.location.search);
        params.set("id", idMaquinaEquip.toString());

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, "", newUrl);

        setIdControlePatrimonio(idMaquinaEquip);
        await loadControlePatrimonioById(idMaquinaEquip);
      }
      pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar máquina/equipamento!"));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    idControlePatrimonio,
    createControlePatrimonioUseCase,
    updateControlePatrimonioUseCase,
    loadControlePatrimonioById,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleControlePatrimonioArquivoModalOpenState = useCallback((idMaquinaEquipArquivo: number | null, openState: boolean) => {
    if (openState) {
      if (idMaquinaEquipArquivo && controlePatrimonioArquivos) {
        setSelectedControlePatrimonioArquivo(controlePatrimonioArquivos.find(a => a.idMaquinaEquipArquivo === idMaquinaEquipArquivo));
      } else {
        setSelectedControlePatrimonioArquivo(undefined);
      }
    }
    setControlePatrimonioArquivoModal(openState);
  }, [
    controlePatrimonioArquivos,
  ]);

  const handleControlePatrimonioArquivoModalSubmit = useCallback(async (form: MaquinaEquipArquivoForm) => {
    if (idControlePatrimonio) {
      try {
        setSaving(true);
        setPageSaving(true);

        if (selectedControlePatrimonioArquivo) {
          await updateMaquinaEquipArquivoUseCase.updateMaquinaEquipArquivo(
            selectedControlePatrimonioArquivo.idMaquinaEquipArquivo,
            idControlePatrimonio,
            form.descricao,
            form.arquivo,
            form.limparArquivo,
          );
        } else {
          await createMaquinaEquipArquivoUseCase.createMaquinaEquipArquivo(
            idControlePatrimonio,
            form.descricao,
            form.arquivo,
          );
        }
        await loadControlePatrimonioArquivos();

        pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
        setControlePatrimonioArquivoModal(false);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao salvar arquivo!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idControlePatrimonio,
    selectedControlePatrimonioArquivo,
    createMaquinaEquipArquivoUseCase,
    updateMaquinaEquipArquivoUseCase,
    loadControlePatrimonioArquivos,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleControlePatrimonioArquivoDownload = useCallback(async (idMaquinaEquipArquivo: number, imageName: string) => {
    if (idMaquinaEquipArquivo) {
      try {
        setLoading(true);
        await downloadControlePatrimonioArquivoUseCase
          .downloadControlePatrimonioArquivo(idMaquinaEquipArquivo, imageName);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao baixar arquivo!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    downloadControlePatrimonioArquivoUseCase,
    pushErrorNotification,
  ]);

  const handleControlePatrimonioArquivoDeleteModalOpenState = useCallback((idMaquinaEquipArquivo: number | null, openState: boolean) => {
    if (openState) {
      if (idMaquinaEquipArquivo && controlePatrimonioArquivos) {
        setSelectedControlePatrimonioArquivo(controlePatrimonioArquivos.find(a => a.idMaquinaEquipArquivo === idMaquinaEquipArquivo));
      } else {
        setSelectedControlePatrimonioArquivo(undefined);
      }
    }
    setControlePatrimonioArquivoDeleteModal(openState);
  }, [
    controlePatrimonioArquivos,
  ]);

  const handleControlePatrimonioArquivoDeleteSubmit = useCallback(async () => {
    if (idControlePatrimonio && selectedControlePatrimonioArquivo) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteMaquinaEquipArquivoUseCase.deleteMaquinaEquipArquivo(
          selectedControlePatrimonioArquivo.idMaquinaEquipArquivo,
          idControlePatrimonio,
        );

        await loadControlePatrimonioArquivos();

        setSelectedControlePatrimonioArquivo(undefined);
        setControlePatrimonioArquivoDeleteModal(false);

        pushInfoNotification("Sucesso", "Registro excluído com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao excluir arquivo!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idControlePatrimonio,
    selectedControlePatrimonioArquivo,
    deleteMaquinaEquipArquivoUseCase,
    loadControlePatrimonioArquivos,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleOpenChangeHistory = useCallback(() => {
    if (idControlePatrimonio) {
      changeHistory.handleOpenChangeHistory(idControlePatrimonio, LogTipoTabela.MAQUINA_EQUIP, true);
    }
  }, [
    idControlePatrimonio,
    changeHistory
  ]);

  return {
    loading,
    saving,
    errorMessage,
    idControlePatrimonio,
    controlePatrimonio: controlePatrimonio ?? null,
    controlePatrimonioArquivos: controlePatrimonioArquivos ?? [],
    selectedControlePatrimonioArquivo: selectedControlePatrimonioArquivo ?? null,
    controlePatrimonioArquivoModal,
    controlePatrimonioArquivoDeleteModal,
    centroCustoDropdown: centroCustoDropdown ?? [],
    fornecedorDropdown: fornecedorDropdown ?? [],
    funcionarioMonitDropdown: funcionarioMonitDropdown ?? [],
    funcionarioRespDropdown: funcionarioRespDropdown ?? [],
    pagination,
    hookForm,
    changeHistory,
    handleReload,
    handleBack,
    handleControlePatrimonioSubmit: hookForm.handleSubmit(handleControlePatrimonioSubmit),
    handleControlePatrimonioArquivoModalOpenState,
    handleControlePatrimonioArquivoModalSubmit,
    handleControlePatrimonioArquivoDownload,
    handleControlePatrimonioArquivoDeleteModalOpenState,
    handleControlePatrimonioArquivoDeleteSubmit,
    handleOpenChangeHistory,
  };
};
